import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, 
  ChevronUp,
  ClipboardList,
  Shield,
  BarChart,
  Settings,
  AlertTriangle,
  FileText,
  Database,
  Activity,
  FileQuestion,
  ClipboardCheck,
  Tool,
  BookOpen,
  Menu,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Zap,
  Target,
  TrendingUp,
  Gem,
  LogOut,
  User,
  Users
} from 'lucide-react';
import { getReleaseIdentity } from '../../constants/buildIdentity';
import { useSidebar } from '../../contexts/SidebarContext';
import { getDeveloperMode, getInternalMode } from '../../utils/developerMode';
import { useAuth } from '../../contexts/AuthContext';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { useAppState } from '../../contexts/AppStateContext';
import { filterAvailableMenuGroups } from './menuAvailability';
import toast from 'react-hot-toast';
import UserProfileMenu from '../Profile/UserProfileMenu';

const Sidebar = () => {
  const releaseIdentity = getReleaseIdentity();
  // Use global app state instead of props
  const { 
    activeModule,
    setActiveModule,
    activeCJSection,
    setActiveCJSection,
    selectedReport,
    setSelectedReport
  } = useAppState();
  // State for collapsible sections
  const [collapsedGroups, setCollapsedGroups] = useState({});
  
  // Use sidebar context for collapse state
  const { isCollapsed, toggleSidebar } = useSidebar();

  // Auth context
  const { user, isAdmin } = useAuth();

  // Module access control
  const { hasModuleAccess, loading: moduleAccessLoading } = useModuleAccess();

  // Check if developer mode is enabled (for E8 Reference Tool only)
  const isDeveloperModeEnabled = getDeveloperMode();
  
  // Check if internal mode is enabled (for Security Services)
  const isInternalModeEnabled = getInternalMode();

  // State for tooltip
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Reports menu data
  const reportsMenu = [
    {
      id: 1,
      name: "Executive Summary",
      description: "High-level overview of Essential Eight compliance status"
    }
  ];

  // Effect to set default report when Reports module is selected
  useEffect(() => {
    if (activeModule === 'Reports' && !selectedReport) {
      setSelectedReport(reportsMenu[0]);
    }
  }, [activeModule, selectedReport, setSelectedReport]);

  // Menu structure with groups and appropriate icons
  const menuGroups = [
    {
      id: 'assessment',
      title: 'ASSESSMENT',
      items: [
        {
          id: 'E8',
          label: 'E8 Tracker',
          icon: BarChart3,
          onClick: () => setActiveModule('E8')
        },
        {
          id: 'E8Light',
          label: 'E8 Light Readiness',
          icon: Zap,
          onClick: () => setActiveModule('E8Light')
        },
        {
          id: 'QRA',
          label: 'Quick Risk Assessment',
          icon: Target,
          onClick: () => setActiveModule('QRA')
        }
      ]
    },
    {
      id: 'reports',
      title: 'REPORTS',
      items: [
        {
          id: 'Reports',
          label: 'Reports',
          icon: TrendingUp,
          onClick: () => setActiveModule('Reports')
        },
        {
          id: 'Vulnerability',
          label: 'Vulnerability',
          icon: AlertTriangle,
          onClick: () => setActiveModule('Vulnerability')
        }
      ]
    },
    // Assets section - now always available (v3.6.0)
    {
      id: 'assets',
      title: 'ASSETS',
      items: [
        {
          id: 'CJ',
          label: 'Crown Jewels',
          icon: Gem,
          onClick: () => setActiveModule('CJ')
        }
      ]
    },
    // Tasks section - E8 task management
    {
      id: 'tasks',
      title: 'TASKS',
      items: [
        {
          id: 'E8Tasks',
          label: 'E8 Tasks',
          icon: ClipboardList,
          onClick: () => setActiveModule('E8Tasks')
        }
      ]
    },
    // Services section - conditionally include only in internal mode
    ...(isInternalModeEnabled ? [{
      id: 'services',
      title: 'SERVICES',
      items: [
        {
          id: 'Services',
          label: 'Services Matrix',
          icon: Shield,
          onClick: () => setActiveModule('Services')
        },
        {
          id: 'ServicesAdmin',
          label: 'Matrix Admin',
          icon: Settings,
          onClick: () => setActiveModule('ServicesAdmin')
        }
      ]
    }] : []),
    // Tools section - conditionally include only in developer mode
    ...(isDeveloperModeEnabled ? [{
      id: 'tools',
      title: 'TOOLS',
      items: [
        {
          id: 'E8Reference',
          label: 'E8 Reference Tool',
          icon: BookOpen,
          onClick: () => setActiveModule('E8Reference')
        }
      ]
    }] : []),
    // Settings section - conditionally include only in developer mode
    ...(isDeveloperModeEnabled ? [{
      id: 'settings',
      title: 'SETTINGS',
      items: [
        {
          id: 'Settings',
          label: 'Settings',
          icon: Settings,
          onClick: () => setActiveModule('Settings')
        }
      ]
    }] : []),
    // Admin section - only show for admin users
    ...(isAdmin ? [{
      id: 'admin',
      title: 'ADMINISTRATION',
      items: [
        {
          id: 'UserManagement',
          label: 'User Management',
          icon: Users,
          onClick: () => setActiveModule('UserManagement')
        }
      ]
    }] : [])
  ];

  const availableMenuGroups = filterAvailableMenuGroups(menuGroups);

  // Toggle group collapse
  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Handle mouse enter for tooltip
  const handleMouseEnter = (item, event) => {
    if (isCollapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.right + 10,
        y: rect.top + (rect.height / 2)
      });
      setHoveredItem(item);
    }
  };

  // Handle mouse leave for tooltip
  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  return (
    <>
      <div 
        className={`bg-[#0A1A2B] text-white flex flex-col h-screen transition-all duration-300 ease-in-out fixed left-0 top-0 z-40 ${
          isCollapsed ? 'w-16' : 'w-64'
        }`}
        style={{ willChange: 'width' }}
      >
        {/* Header with toggle button */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl font-bold">E8 Tracker</h1>
              {/* Developer mode indicator */}
              {isDeveloperModeEnabled && (
                <span className="px-2 py-1 text-xs bg-orange-600 text-white rounded-md font-medium">
                  DEV
                </span>
              )}
              {/* Internal mode indicator */}
              {isInternalModeEnabled && (
                <span className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md font-medium">
                  INTERNAL
                </span>
              )}
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-slate-700 rounded-md transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation Menu */}
        <div className="flex-1 overflow-y-auto">
          {availableMenuGroups.filter(group => {
            if (moduleAccessLoading) return false;
            
            // Filter out groups where user has no access to any items
            return group.items.some(item => hasModuleAccess(item.id));
          }).map(group => (
            <div key={group.id} className="mb-4">
              {/* Group Header */}
              {!isCollapsed && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full px-4 py-2 flex items-center justify-between text-sm text-gray-400 hover:text-white transition-colors"
                >
                  <span className="font-medium">{group.title}</span>
                  {collapsedGroups[group.id] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronUp className="w-4 h-4" />
                  )}
                </button>
              )}

              {/* Section divider for collapsed state */}
              {isCollapsed && group.id !== 'assessment' && (
                <div className="mx-2 border-t border-gray-600 my-2"></div>
              )}

              {/* Group Items */}
              <div className={`space-y-1 ${!isCollapsed && collapsedGroups[group.id] ? 'hidden' : ''}`}>
                {group.items.filter(item => {
                  if (moduleAccessLoading) return false;
                  
                  // Check if user has access to this module
                  return hasModuleAccess(item.id);
                }).map(item => {
                  const Icon = item.icon;
                  const isActive = activeModule === item.id;
                  const isDisabled = item.disabled === true;

                  return (
                    <div key={item.id}>
                      <button
                        onClick={isDisabled ? undefined : item.onClick}
                        onMouseEnter={(e) => handleMouseEnter(item, e)}
                        onMouseLeave={handleMouseLeave}
                        className={`w-full flex items-center text-sm transition-colors relative
                          ${isCollapsed ? 'p-3 justify-center' : 'px-4 py-2 space-x-3'}
                          ${isActive 
                            ? 'bg-blue-600 text-white' 
                            : isDisabled
                              ? 'text-gray-600 cursor-not-allowed opacity-50'
                              : 'text-gray-400 hover:text-white hover:bg-[#1E293B]'}`}
                        aria-label={isCollapsed ? item.label : undefined}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        {!isCollapsed && <span>{item.label}</span>}
                      </button>
                      
                      {/* Show Reports submenu only when expanded and active */}
                      {!isCollapsed && item.id === 'Reports' && activeModule === 'Reports' && (
                        <div className="pl-8 mt-1 space-y-1">
                          {reportsMenu.map(report => (
                            <button
                              key={report.id}
                              onClick={() => setSelectedReport(report)}
                              className={`w-full px-4 py-2 text-left text-sm rounded-md transition-colors
                                ${selectedReport?.id === report.id
                                  ? 'bg-blue-500 text-white'
                                  : 'text-gray-400 hover:text-white hover:bg-[#1E293B]'}`}
                            >
                              {report.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* User Profile Section */}
        <div className="p-4 border-t border-gray-700">
          {!isCollapsed ? (
            <div className="space-y-3">
              <UserProfileMenu />
              
              {/* Version information */}
              <div className="text-center">
                <div className="text-xs font-medium text-gray-400">E8 Tracker v{releaseIdentity.version}</div>
                <div className="text-xs text-gray-400">{releaseIdentity.contextLabel} · Revision {releaseIdentity.revision}</div>
                <div className="text-xs text-gray-400">Built {releaseIdentity.builtAt}</div>
              </div>
            </div>
          ) : (
            /* Collapsed state - simple user icon */
            <div className="flex justify-center">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip for collapsed state */}
      {isCollapsed && hoveredItem && (
        <div
          className="fixed z-50 bg-gray-900 text-white px-3 py-2 rounded-md text-sm font-medium shadow-lg pointer-events-none transition-opacity duration-200"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translateY(-50%)'
          }}
        >
          {hoveredItem.label}
          <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900"></div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
